import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CustomerCard from "@/components/CustomerCard";
import FollowupCalendar from "@/components/FollowupCalendar";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtDate, todayISO } from "@/lib/pgt";
import { Loader2, CalendarClock, CheckCircle2, List, CalendarDays, Users, User } from "lucide-react";
import { toast } from "sonner";

export default function Followups() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isManager = user.role === "admin" || user.role === "manager";
  const [view, setView] = useState("calendar");
  const [scope, setScope] = useState("mine");
  const [salesperson, setSalesperson] = useState("all");

  const { data: team = [] } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
    enabled: isManager,
  });
  const salespeople = team.filter((u) => u.role === "executive" || u.role === "manager");

  const { data: followups = [], isLoading } = useQuery({
    queryKey: ["followups", scope, salesperson],
    queryFn: async () => {
      const params = { scope };
      if (isManager && salesperson !== "all") params.salesperson_id = salesperson;
      return (await api.get("/followups", { params })).data;
    },
  });

  const today = todayISO();
  const overdue = followups.filter((f) => f.due_date < today);
  const dueToday = followups.filter((f) => f.due_date === today);
  const upcoming = followups.filter((f) => f.due_date > today);

  const complete = async (id) => {
    await api.patch(`/followups/${id}`, { status: "completed" });
    toast.success("Follow-up completed");
    qc.invalidateQueries({ queryKey: ["followups"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };
  const Section = ({ title, items, tone }) => items.length > 0 && (
    <div className="mb-8">
      <h2 className={`mb-3 font-heading text-lg font-semibold ${tone}`}>{title} <span className="text-stone-400">({items.length})</span></h2>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((f) => (
          <div key={f.id} className="relative">
            <CustomerCard customer={f.customer} note={`${fmtDate(f.due_date)} · ${f.reason || "Follow up"}`} />
            <Button size="sm" variant="outline" className="absolute right-3 bottom-3 h-7 text-xs" onClick={(e) => { e.stopPropagation(); complete(f.id); }} data-testid={`complete-followup-${f.id}`}>
              <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Done
            </Button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="animate-fade-up">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-stone-900">Follow-ups</h1>
          <p className="mt-1 text-sm text-stone-500">
            {followups.length} pending{scope === "team" ? " across the team" : " in your queue"}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isManager && (
            <div className="flex rounded-lg border border-stone-200 bg-white p-0.5">
              <button
                onClick={() => { setScope("mine"); setSalesperson("all"); }}
                data-testid="followups-scope-mine"
                className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium ${scope === "mine" && salesperson === "all" ? "bg-forest text-ivory" : "text-stone-500"}`}
              >
                <User className="h-3.5 w-3.5" /> Mine
              </button>
              <button
                onClick={() => { setScope("team"); setSalesperson("all"); }}
                data-testid="followups-scope-team"
                className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium ${scope === "team" && salesperson === "all" ? "bg-forest text-ivory" : "text-stone-500"}`}
              >
                <Users className="h-3.5 w-3.5" /> Team
              </button>
            </div>
          )}
          {isManager && (
            <Select value={salesperson} onValueChange={(v) => { setSalesperson(v); if (v !== "all") setScope("team"); }}>
              <SelectTrigger className="h-9 w-44 bg-white" data-testid="followups-salesperson-filter"><SelectValue placeholder="By salesperson" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All salespeople</SelectItem>
                {salespeople.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <div className="flex rounded-lg border border-stone-200 bg-white p-0.5">
            <button
              onClick={() => setView("calendar")}
              data-testid="followups-view-calendar"
              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium ${view === "calendar" ? "bg-forest text-ivory" : "text-stone-500"}`}
            >
              <CalendarDays className="h-3.5 w-3.5" /> Calendar
            </button>
            <button
              onClick={() => setView("list")}
              data-testid="followups-view-list"
              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium ${view === "list" ? "bg-forest text-ivory" : "text-stone-500"}`}
            >
              <List className="h-3.5 w-3.5" /> List
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-forest" /></div>
      ) : view === "calendar" ? (
        <FollowupCalendar followups={followups} onComplete={complete} />
      ) : followups.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 bg-white/50 py-16 text-center">
          <CalendarClock className="mx-auto h-10 w-10 text-stone-300" />
          <p className="mt-3 text-sm text-stone-500">No pending follow-ups. You're all caught up.</p>
        </div>
      ) : (
        <>
          <Section title="Overdue" items={overdue} tone="text-red-600" />
          <Section title="Due today" items={dueToday} tone="text-forest" />
          <Section title="Upcoming" items={upcoming} tone="text-stone-800" />
        </>
      )}
    </div>
  );
}
